import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import ContactForm from '../components/ContactForm'

export default function Home() {
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(true)

  useEffect(() => {
    // Fetch featured projects from CMS
    fetch('https://api.dev.auxo.dk/public/v2/content/projekter?limit=3')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          setProjects(data.items)
        }
        setLoadingProjects(false)
      })
      .catch(() => setLoadingProjects(false))
  }, [])

  return (
    <>
      <SEOHead
        title="Agstner Byg ApS - Professionelt tømrerarbejde, tag og renovering"
        description="Agstner Byg ApS leverer solide og gennemarbejdede løsninger til både mindre og større byggeopgaver i Vissenbjerg, Odense og på Fyn. Få et uforpligtende tilbud."
      />

      {/* 1. HERO SECTION (Industrial-16 Style) */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-zinc-950 pt-10 pb-20 rounded-none border-b border-zinc-800">
        {/* Dark Background image with gradient tint */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00005_d995d540.jpeg"
            alt="Agstner Byg ApS tømrerarbejde"
            className="w-full h-full object-cover object-center opacity-70 grayscale-20 brightness-95 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 via-zinc-950/35 to-zinc-950/15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12">
          <div className="max-w-3xl space-y-6">
            
            {/* Top accent row */}
            <div className="flex items-center gap-3">
              <span className="w-8 h-1 bg-orange-500"></span>
              <span className="font-display font-bold text-xs sm:text-sm text-orange-500 uppercase tracking-widest">
                Agstner Byg ApS · Vissenbjerg & Fyn
              </span>
            </div>

            {/* Main H1 Title */}
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-display font-black text-white uppercase tracking-tight leading-none">
              Professionelt tømrerarbejde, tag og renovering
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg text-zinc-300 font-normal leading-relaxed max-w-2xl">
              Agstner Byg ApS leverer solide og gennemarbejdede løsninger til både mindre og større byggeopgaver.
            </p>

            {/* CTA Buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-sm uppercase tracking-widest transition-all duration-200 flex items-center gap-2.5 border border-orange-500"
              >
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
                <span>Få et tilbud</span>
              </Link>

              <a
                href="tel:+4552502543"
                className="px-6 py-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-orange-500 font-display font-bold text-sm uppercase tracking-wider border border-zinc-800 hover:border-orange-500 transition-all flex items-center gap-2.5 whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
              </a>

              <Link
                to="/projekter"
                className="px-5 py-4 text-zinc-300 hover:text-white font-display font-bold text-sm uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                <span>Se vores projekter</span>
                <i className="fa-solid fa-arrow-right text-xs text-orange-500" aria-hidden="true"></i>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* 3. FEATURED SERVICES ACCORDION STACK (Dark Theme - Industrial-16 Style) */}
      <section className="py-24 bg-zinc-950 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Row */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 pb-8 border-b border-zinc-800">
            <div>
              <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest mb-2">
                <span className="w-6 h-0.5 bg-orange-500"></span>
                <span>VORES TRE PRIMÆRE ARBEJDSOMRÅDER</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-display font-bold text-white uppercase tracking-tight">
                Hvad kan Agstner Byg ApS hjælpe dig med?
              </h2>
            </div>
            <p className="text-zinc-400 text-sm max-w-md">
              Klik på det enkelte område for at læse mere om vores løsninger inden for tømrerarbejde, tag og renovering.
            </p>
          </div>

          {/* Accordion List / Grid Stack */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Service 1: Tømrerarbejde */}
            <Link
              to="/tomrerarbejde"
              className="bg-zinc-900 border border-zinc-800 p-8 flex flex-col justify-between space-y-6 hover:border-orange-500 transition-colors group block cursor-pointer"
            >
              <div className="space-y-4">
                <div className="h-48 overflow-hidden my-2 border border-zinc-800">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00028_99cb79be.jpeg"
                    alt="Tømrerarbejde"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-display font-bold text-2xl text-white uppercase group-hover:text-orange-500 transition-colors">
                  Tømrerarbejde
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Professionelt tømrerarbejde udført med præcision og fokus på langtidsholdbare løsninger i træ. Montering af trækonstruktioner, døre og vinduer, træterrasser og udvendige træfacader.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <span className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-widest text-orange-500 group-hover:text-orange-400 transition-colors">
                  <span>Læs mere om Tømrerarbejde</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </span>
              </div>
            </Link>

            {/* Service 2: Tag */}
            <Link
              to="/tag"
              className="bg-zinc-900 border border-zinc-800 p-8 flex flex-col justify-between space-y-6 hover:border-orange-500 transition-colors group block cursor-pointer"
            >
              <div className="space-y-4">
                <div className="h-48 overflow-hidden my-2 border border-zinc-800">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00056.jpeg"
                    alt="Tagarbejde"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-display font-bold text-2xl text-white uppercase group-hover:text-orange-500 transition-colors">
                  Tag
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Nyt tag, tagrenovering og undertagsløsninger tilpasset danske vejrforhold. Lægning af tagsten, reparation, opbygning af undertag, montage af kviste samt isætning af tagvinduer.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <span className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-widest text-orange-500 group-hover:text-orange-400 transition-colors">
                  <span>Læs mere om Tagarbejde</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </span>
              </div>
            </Link>

            {/* Service 3: Renovering */}
            <Link
              to="/renovering"
              className="bg-zinc-900 border border-zinc-800 p-8 flex flex-col justify-between space-y-6 hover:border-orange-500 transition-colors group block cursor-pointer"
            >
              <div className="space-y-4">
                <div className="h-48 overflow-hidden my-2 border border-zinc-800">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00007.jpeg"
                    alt="Renovering"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-display font-bold text-2xl text-white uppercase group-hover:text-orange-500 transition-colors">
                  Renovering
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Gennemgribende boligrenovering og modernisering. Både indvendig og udvendig renovering, ombygning, modernisering af værelser samt udskiftning af slidte bygningsdele.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <span className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-widest text-orange-500 group-hover:text-orange-400 transition-colors">
                  <span>Læs mere om Renovering</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </span>
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* 4. FULL-WIDTH BANNER ("AGSTNER BYG APS" / INDUSTRIAL STATEMENT) */}
      <section className="relative py-28 bg-zinc-950 border-b border-zinc-800 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00068.jpeg"
            alt="Agstner Byg ApS"
            className="w-full h-full object-cover opacity-45 grayscale"
          />
          <div className="absolute inset-0 bg-zinc-950/50" />
        </div>

        {/* Industrial background watermarked logo */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 z-0 opacity-15 select-none pointer-events-none hidden lg:block">
          <img
            src="https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/ag-logo.png"
            alt=""
            className="h-72 w-auto object-contain filter invert brightness-200"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-2xl space-y-3">
            <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest">
              <span className="w-6 h-0.5 bg-orange-500"></span>
              <span>KVALITET I ALLE DETALJER</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-bold text-white uppercase tracking-tight">
              KVALITETSBYGGERI OG SOLIDT HÅNDVÆRK PÅ FYN
            </h2>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Solidt håndværk, personlig service og direkte kontakt på hele Fyn.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/kontakt"
              className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all border border-orange-500"
            >
              Få et tilbud
            </Link>
            <a
              href="tel:+4552502543"
              className="px-6 py-4 rounded-none bg-zinc-900 text-orange-500 hover:text-orange-400 font-display font-bold text-xs uppercase tracking-widest border border-zinc-800 transition-all whitespace-nowrap"
            >
              Ring direkte på 52 50 25 43
            </a>
          </div>
        </div>
      </section>

      {/* 5. INTRODUCTION & VALUES SECTION (Light Theme - Industrial-16 Style) */}
      <section className="py-24 bg-white text-zinc-900 border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Image Col */}
            <div className="lg:col-span-5 relative">
              <div className="relative border border-zinc-300 bg-stone-100 p-0 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00026.jpeg"
                  alt="Agstner Byg ApS Tømrerarbejde Vissenbjerg"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </div>

            {/* Text Col */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-2 text-orange-600 font-display text-xs uppercase tracking-widest font-bold">
                <span className="w-6 h-0.5 bg-orange-500"></span>
                <span>Om Agstner Byg ApS</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-zinc-900 uppercase tracking-tight">
                Solidt håndværk, personlig service og direkte kontakt
              </h2>
              
              <p className="text-zinc-700 text-sm leading-relaxed">
                Agstner Byg ApS leverer solide og gennemarbejdede løsninger til både mindre og større byggeopgaver. Vi er beliggende på Odensevej 36 i Vissenbjerg og udfører opgaver for private og erhverv over hele Fyn.
              </p>
              
              <p className="text-zinc-600 text-xs leading-relaxed">
                Vores arbejde bygger på grundighed, god dialog og stor respekt for håndværksmæssig præcision. Hvad end det gælder nyt tag, en større boligrenovering eller udskiftning af døre og vinduer, sikrer vi et flot og langtidsholdbart resultat.
              </p>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-agstner-byg"
                  className="px-6 py-3.5 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-display font-bold text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-arrow-right text-xs text-orange-500" aria-hidden="true"></i>
                </Link>

                <Link
                  to="/kontakt"
                  className="px-6 py-3.5 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2"
                >
                  <span>Få et tilbud</span>
                </Link>

                <a
                  href="tel:+4552502543"
                  className="text-orange-600 hover:text-orange-700 font-display font-bold text-xs uppercase tracking-wider flex items-center gap-2 whitespace-nowrap py-2"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring direkte på 52 50 25 43</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 6. FEATURED PROJECTS SECTION (Dark Theme) */}
      <section className="py-24 bg-zinc-950 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 pb-6 border-b border-zinc-800">
            <div>
              <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest mb-1">
                <span className="w-6 h-0.5 bg-orange-500"></span>
                <span>UDFØRT HÅNDVÆRK</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-display font-bold text-white uppercase tracking-tight">
                Udvalgte projekter
              </h2>
            </div>
            <Link
              to="/projekter"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-none bg-zinc-900 hover:bg-zinc-800 text-orange-500 font-display font-bold text-xs uppercase tracking-widest border border-zinc-800 transition-colors"
            >
              <span>Se alle projekter</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.length > 0 ? (
              projects.map((item, idx) => (
                <div
                  key={item.id}
                  className="bg-zinc-900 border border-zinc-800 overflow-hidden flex flex-col group hover:border-orange-500 transition-all"
                >
                  <div className="relative h-60 overflow-hidden border-b border-zinc-800">
                    <img
                      src={
                        Array.isArray(item.hovedbillede) && item.hovedbillede[0]
                          ? item.hovedbillede[0]
                          : 'https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/agstner-hero-carpentry.jpg'
                      }
                      alt={item.projektnavn}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <span className="text-xs text-zinc-400 font-mono uppercase tracking-wider flex items-center gap-1.5 mb-1">
                        <i className="fa-solid fa-location-dot text-orange-500" aria-hidden="true"></i>
                        {item.omraade || 'Fyn'}
                      </span>
                      <h3 className="font-display font-bold text-xl text-white uppercase group-hover:text-orange-500 transition-colors">
                        {item.projektnavn}
                      </h3>
                      <p className="text-zinc-400 text-xs mt-2 line-clamp-3 leading-relaxed">
                        {item.kort_beskrivelse}
                      </p>
                    </div>
                    <div className="pt-4 border-t border-zinc-800 flex items-center justify-between">
                      <Link
                        to={`/projekter/${item.url_slug || item.id}`}
                        className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline flex items-center gap-1"
                      >
                        Se detaljer <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                      </Link>
                      <Link
                        to="/kontakt"
                        className="text-xs bg-zinc-950 hover:bg-orange-500 hover:text-zinc-950 text-orange-500 font-display font-bold uppercase tracking-widest px-3 py-1.5 border border-zinc-800 transition-all"
                      >
                        Få et tilbud
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-12 text-center text-zinc-500 font-mono text-xs uppercase">
                Indlæser projekter...
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 7. QUALITY AND PERSONAL SERVICE VALUES (Design Reference Layout - Light Theme) */}
      <section className="py-24 bg-stone-100 text-zinc-900 border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-2">
            <div className="flex items-center justify-center gap-2 text-orange-600 font-display text-xs uppercase tracking-widest font-bold">
              <span className="w-6 h-0.5 bg-orange-500"></span>
              <span>KVALITET & PERSONLIG SERVICE</span>
              <span className="w-6 h-0.5 bg-orange-500"></span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-zinc-900 uppercase tracking-tight">
              Vores værdier i det daglige håndværk
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-300">
            
            {/* Feature 1 */}
            <div className="flex flex-col items-center text-center px-6 sm:px-10 py-8 space-y-4">
              <i className="fa-regular fa-circle-check text-4xl text-orange-600" aria-hidden="true"></i>
              <h3 className="font-display font-bold text-xl text-zinc-900 uppercase tracking-tight">
                Godt håndværk
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed max-w-xs">
                Vi slår ikke af på kvaliteten og sørger for, at alle samlinger og detaljer udføres korrekt.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="flex flex-col items-center text-center px-6 sm:px-10 py-8 space-y-4">
              <i className="fa-regular fa-comments text-4xl text-orange-600" aria-hidden="true"></i>
              <h3 className="font-display font-bold text-xl text-zinc-900 uppercase tracking-tight">
                Tæt dialog
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed max-w-xs">
                Kunden er altid i direkte kontakt med håndværkeren. Vi lytter og rådgiver gennem hele processen.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="flex flex-col items-center text-center px-6 sm:px-10 py-8 space-y-4">
              <i className="fa-regular fa-handshake text-4xl text-orange-600" aria-hidden="true"></i>
              <h3 className="font-display font-bold text-xl text-zinc-900 uppercase tracking-tight">
                Erfaring & Tryghed
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed max-w-xs">
                Vi afleverer altid byggepladsen opryddet og sikrer en gennemskuelig og uforpligtende dialog om din opgave.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 8. CAPABILITIES & CONTACT PROMPT (Industrial-16 Split Layout) */}
      <section className="py-24 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Info Col */}
            <div className="lg:col-span-5 space-y-6">
              <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest">
                <span className="w-6 h-0.5 bg-orange-500"></span>
                <span>KONTAKT AGSTNER BYG APS</span>
              </div>
              
              <h2 className="text-3xl sm:text-5xl font-display font-bold text-white uppercase tracking-tight">
                Lad os drøfte dit næste byggeprojekt
              </h2>
              
              <p className="text-zinc-300 text-sm leading-relaxed">
                Uanset om du planlægger ny træterrasse, udskiftning af tag eller gennemtænkt renovering af huset, står vi klar til at rådgive dig.
              </p>

              <div className="space-y-4 pt-2">
                <a
                  href="tel:+4552502543"
                  className="flex items-center gap-4 p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-xl group-hover:scale-105 transition-transform flex-shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-400 font-mono block uppercase tracking-widest">Direkte telefon</span>
                    <span className="text-xl sm:text-2xl font-display font-black text-orange-500 group-hover:text-orange-400 transition-colors whitespace-nowrap">
                      52 50 25 43
                    </span>
                  </div>
                </a>

                <a
                  href="mailto:Agstnerbyg@icloud.com"
                  className="flex items-center gap-4 p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-xl group-hover:scale-105 transition-transform flex-shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-400 font-mono block uppercase tracking-widest">Send en e-mail</span>
                    <span className="text-base font-bold text-white group-hover:text-orange-500 transition-colors whitespace-nowrap">
                      Agstnerbyg@icloud.com
                    </span>
                  </div>
                </a>

                <a
                  href="https://www.facebook.com/profile.php?id=61590981091175"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-xl group-hover:scale-105 transition-transform flex-shrink-0">
                    <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-400 font-mono block uppercase tracking-widest">Følg os online</span>
                    <span className="text-base font-bold text-white group-hover:text-orange-500 transition-colors whitespace-nowrap inline-flex items-center gap-1.5">
                      Facebook profil
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs text-orange-500" aria-hidden="true"></i>
                    </span>
                  </div>
                </a>

                <div className="p-5 bg-zinc-900/60 border border-zinc-800/80 text-xs text-zinc-300 space-y-1">
                  <p className="font-display font-bold text-white uppercase">Agstner Byg ApS</p>
                  <p>Odensevej 36, 5492 Vissenbjerg</p>
                  <p className="text-zinc-500 font-mono text-[11px] pt-1">CVR: 44000997</p>
                </div>
              </div>
            </div>

            {/* Form Col */}
            <div className="lg:col-span-7 bg-zinc-900 border border-zinc-800 p-0">
              <ContactForm />
            </div>

          </div>
        </div>
      </section>
    </>
  )
}
